import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScheduleRuleStore, type ScheduleRuleDto } from '../../../store/useScheduleRuleStore';
import { useToastStore } from '../../../store/useToastStore';
import { Card } from '../../../components/Shared/Card';
import { Button } from '../../../components/Shared/Button';
import { Badge } from '../../../components/Shared/Badge';
import { Modal } from '../../../components/Shared/Modal';
import { RefreshCw, Plus, Pencil, Trash2, Repeat } from 'lucide-react';
import {
  ScheduleRuleTypeLabels,
  ScheduleRuleTypeDescriptions,
  formatScheduleRule,
} from '../../../utils/scheduleRuleConstants';
import { ScheduleRuleFields } from './ScheduleRuleFields';
import { getRuleIcon, getRuleBadgeVariant } from './scheduleRuleDisplay';
import { useScheduleRuleForm } from './useScheduleRuleForm';

interface ScheduleRulesManagerProps {
  taskId: string;
}

export function ScheduleRulesManager({ taskId }: ScheduleRulesManagerProps) {
  const { t } = useTranslation(['tasks', 'common']);
  const { rules, loading, fetchRules, createRule, updateRule, deleteRule } = useScheduleRuleStore();
  const { addToast } = useToastStore();

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ScheduleRuleDto | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteRuleId, setDeleteRuleId] = useState<string | null>(null);

  const form = useScheduleRuleForm();

  useEffect(() => {
    fetchRules(taskId);
  }, [taskId, fetchRules]);

  const openCreate = () => {
    form.reset();
    setEditingRule(null);
    setIsFormModalOpen(true);
  };

  const openEdit = (rule: ScheduleRuleDto) => {
    form.loadFrom(rule);
    setEditingRule(rule);
    setIsFormModalOpen(true);
  };

  const closeForm = () => {
    setIsFormModalOpen(false);
    setEditingRule(null);
    form.reset();
  };

  const handleSubmit = async () => {
    try {
      if (editingRule?.taskScheduleRuleId) {
        await updateRule(taskId, editingRule.taskScheduleRuleId, form.buildUpdate(editingRule.isActive ?? true));
        addToast(t('tasks:scheduleRulesManager.messages.updated'), 'success');
      } else {
        await createRule(taskId, form.buildCreate());
        addToast(t('tasks:scheduleRulesManager.messages.created'), 'success');
      }
      closeForm();
    } catch {
      addToast(
        editingRule
          ? t('tasks:scheduleRulesManager.messages.updateError')
          : t('tasks:scheduleRulesManager.messages.createError'),
        'error',
      );
    }
  };

  const handleDelete = async () => {
    if (!deleteRuleId) return;
    try {
      await deleteRule(taskId, deleteRuleId);
      addToast(t('tasks:scheduleRulesManager.messages.deleted'), 'success');
      setIsDeleteModalOpen(false);
      setDeleteRuleId(null);
    } catch {
      addToast(t('tasks:scheduleRulesManager.messages.deleteError'), 'error');
    }
  };

  const confirmDelete = (ruleId: string) => {
    setDeleteRuleId(ruleId);
    setIsDeleteModalOpen(true);
  };

  return (
    <>
      <Card
        title={t('tasks:scheduleRulesManager.title')}
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => fetchRules(taskId)}
              disabled={loading}
              className="!py-1.5 !px-3 text-sm"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              {t('tasks:scheduleRulesManager.refresh')}
            </Button>
            <Button onClick={openCreate} className="!py-1.5 !px-3 text-sm">
              <Plus className="w-4 h-4 mr-1" />
              {t('tasks:scheduleRulesManager.addRule')}
            </Button>
          </div>
        }
      >
        {rules.length === 0 && !loading ? (
          <div className="text-center py-6">
            <Repeat className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('tasks:scheduleRulesManager.empty')}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {t('tasks:scheduleRulesManager.emptyDescription')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map((rule) => (
              <div
                key={rule.taskScheduleRuleId}
                className="flex items-center justify-between py-3 px-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 text-gray-500 dark:text-gray-400">
                    {getRuleIcon(rule.type)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white text-sm">
                        {formatScheduleRule(rule)}
                      </span>
                      <Badge variant={getRuleBadgeVariant(rule.type)}>
                        {rule.type !== undefined ? ScheduleRuleTypeLabels[rule.type] || t('tasks:scheduleRulesManager.unknown') : t('tasks:scheduleRulesManager.unknown')}
                      </Badge>
                      {rule.isActive === false && (
                        <Badge variant="default">{t('tasks:scheduleRulesManager.inactive')}</Badge>
                      )}
                    </div>
                    {rule.type !== undefined && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {ScheduleRuleTypeDescriptions[rule.type]}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(rule)}
                    title={t('tasks:scheduleRulesManager.edit')}
                    className="p-1 sm:p-2"
                  >
                    <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => confirmDelete(rule.taskScheduleRuleId!)}
                    title={t('tasks:scheduleRulesManager.delete')}
                    className="p-1 sm:p-2"
                  >
                    <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Add / Edit Rule Modal */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={closeForm}
        title={editingRule ? t('tasks:scheduleRulesManager.editRuleTitle') : t('tasks:scheduleRulesManager.addRuleTitle')}
      >
        <div className="space-y-5">
          <ScheduleRuleFields form={form} />

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <Button variant="secondary" onClick={closeForm}>
              {t('tasks:scheduleRulesManager.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading
                ? t('tasks:scheduleRulesManager.saving')
                : editingRule
                  ? t('tasks:scheduleRulesManager.saveRule')
                  : t('tasks:scheduleRulesManager.createRule')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteRuleId(null); }}
        title={t('tasks:scheduleRulesManager.deleteRuleTitle')}
      >
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            {t('tasks:scheduleRulesManager.confirmDelete')}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('tasks:scheduleRulesManager.confirmDeleteDescription')}
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => { setIsDeleteModalOpen(false); setDeleteRuleId(null); }}
            >
              {t('tasks:scheduleRulesManager.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={loading}>
              {loading ? t('tasks:scheduleRulesManager.deleting') : t('tasks:scheduleRulesManager.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
